import React, { FC, useEffect, useState } from "react";
import { isEqual } from "lodash";

import { AutoCompleteSuggestionsInput, IconButton, Input, Suggestion, TitleHeader, Tooltip } from "@xapp/components";
import { AppReferenceIdInput } from "generated/graphql";

import { INTEGRATIONS } from "./IntegrationDropdown";

// Filter out "none", "other", and convert to Suggestion format for autocomplete
const SERVICE_SUGGESTIONS: Suggestion[] = INTEGRATIONS
    .filter((i) => i.id !== "none" && i.id !== "other")
    .map((i) => ({ id: i.id, label: i.name }));

export interface ReferenceIdSettingsProps {
    referenceId?: AppReferenceIdInput | null;
    saving?: boolean;
    onSave?(referenceId: AppReferenceIdInput | null): void;
}

export const ReferenceIdSettings: FC<ReferenceIdSettingsProps> = (props) => {
    const { referenceId, saving, onSave } = props;

    const [mutableReferenceId, setMutableReferenceId] = useState<AppReferenceIdInput>({
        service: referenceId?.service || "",
        id: referenceId?.id || "",
    });

    useEffect(() => {
        setMutableReferenceId({
            service: referenceId?.service || "",
            id: referenceId?.id || "",
        });
    }, [referenceId]);

    const handleSave = () => {
        if (onSave) {
            // If both fields are empty, save as null
            if (mutableReferenceId.service.trim() === "" && mutableReferenceId.id.trim() === "") {
                onSave(null);
            } else {
                onSave({
                    service: mutableReferenceId.service.trim(),
                    id: mutableReferenceId.id.trim(),
                });
            }
        }
    };

    // Compare current state to original for dirty detection
    const originalState = {
        service: referenceId?.service || "",
        id: referenceId?.id || "",
    };
    const dirty = !isEqual(originalState, mutableReferenceId);

    return (
        <div style={{ marginTop: "32px" }}>
            <TitleHeader value="Reference ID">
                <Tooltip content={dirty ? "Save" : "No changes"}>
                    <IconButton
                        color="primary"
                        iconName="save"
                        onClick={handleSave}
                        disabled={!dirty || saving}
                    />
                </Tooltip>
            </TitleHeader>

            <p style={{ color: "#666", marginBottom: "16px", fontSize: "14px" }}>
                Add an external service ID to reference this app in another system.
            </p>

            <div style={{ display: "flex", gap: "16px", alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: "1", minWidth: "200px" }}>
                    <AutoCompleteSuggestionsInput
                        label="Service"
                        suggestions={SERVICE_SUGGESTIONS}
                        echo={true}
                        variation="outlined"
                        value={mutableReferenceId.service}
                        placeholder="Select or enter a service"
                        onChange={(selection) => {
                            const value = typeof selection === "string" ? selection : selection.label;
                            setMutableReferenceId({ ...mutableReferenceId, service: value });
                        }}
                    />
                </div>
                <div style={{ flex: "1", minWidth: "200px" }}>
                    <Input
                        label="ID"
                        name="id"
                        variation="outlined"
                        value={mutableReferenceId.id}
                        onChange={(value) => setMutableReferenceId({ ...mutableReferenceId, id: value as string })}
                        helperText="The ID in the external service"
                    />
                </div>
            </div>
        </div>
    );
};
